// bluaIQ — Project Calendar (Bloque 3)
// Real Google-Calendar-style view with Month/Week/Day modes.
// Aggregates payments, RFIs, change orders, lifecycle task starts/ends,
// and auto-generated "Order materials" reminders from the backend
// /api/projects/:id/calendar-events endpoint.

import { apiFetch } from '../utils/api'
import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useParams, Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@clerk/clerk-react'
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Package, Shield, Info } from 'lucide-react'
import { formatMoneyValue } from '../utils/money'
import { TASK_LABELS_BY_TEXT } from '../utils/ganttTaskLabels'
import {
  format, startOfMonth, endOfMonth, startOfWeek, endOfWeek,
  addDays, addMonths, addWeeks, isSameMonth, isToday,
} from 'date-fns'
import { es as esLocale } from 'date-fns/locale'

// Localize TASK_START / TASK_END event titles that the backend pre-constructs as
// "${prefix}: ${stored Spanish task name}". The stored name is always the Spanish
// seed value regardless of the requested language. We strip the prefix, look up
// the stored name in the bilingual map, and reconstruct the localized title.
// Falls back to the raw title for custom/unknown task names — never blank.
function localizeCalendarTaskTitle(title: string, isEs: boolean): string {
  // Match any of the four prefix forms the backend may send.
  const PREFIXES = [
    { strip: 'Start: ',  en: 'Start', es: 'Inicio' },
    { strip: 'End: ',    en: 'End',   es: 'Fin'    },
    { strip: 'Inicio: ', en: 'Start', es: 'Inicio' },
    { strip: 'Fin: ',    en: 'End',   es: 'Fin'    },
  ]
  for (const p of PREFIXES) {
    if (title.startsWith(p.strip)) {
      const storedName = title.slice(p.strip.length)
      const bil = TASK_LABELS_BY_TEXT[storedName]
      if (!bil) return title // unknown or custom task — leave raw
      return `${isEs ? p.es : p.en}: ${isEs ? bil.es : bil.en}`
    }
  }
  return title // not a TASK_START/END title — leave raw
}

type ViewMode = 'month' | 'week' | 'day'

type CalendarEvent = {
  id: string
  date: string
  type:
    | 'PAYMENT'
    | 'RFI_DUE'
    | 'CHANGE_ORDER'
    | 'TASK_START'
    | 'TASK_END'
    | 'ORDER_MATERIALS'
    | 'INSPECTION_SCHEDULED'
    | 'INSPECTION_FAILED'
    | 'INSPECTION_RETRY'
    | 'INSPECTION_PASSED'
    | 'INSPECTION_READY'
    | 'INSPECTION_BLOCKED'
    | 'SELECTION_DEADLINE'
    // PR-NEW: insurance reminder derived events (Phase 1).
    | 'INSURANCE_EXPIRING_SOON'
    | 'INSURANCE_EXPIRATION'
    | 'INSURANCE_EXPIRED'
    // Insurance start-of-coverage informational event.
    | 'INSURANCE_EFFECTIVE_START'
  title: string
  description?: string
  amount?: number
  status?: string
  color: string
  link: string
  category?: string
  itemCount?: number
  // PR-NEW: severity (info | warning | critical) for the
  // insurance-reminder branches; ignored for other event types.
  severity?: 'info' | 'warning' | 'critical'
}

// PR-NEW: shortlist row for the sidebar "Next insurance renewal"
// panel. Range-independent — these expirations may sit YEARS
// past the visible month grid. Computed server-side from the
// project's Insurance rows via getNextInsuranceRenewals().
type NextInsuranceRenewal = {
  policyId: string
  policyType: string                 // raw enum: GENERAL_LIABILITY | BUILDERS_RISK | …
  policyTypeLabel: string            // localized pretty name (server picks isEs)
  carrier: string
  policyNumber: string | null
  coverageAmount: number | null
  endDate: string                    // ISO datetime
  daysUntilExpiration: number
  derivedStatus: 'EXPIRED' | 'EXPIRING_SOON' | 'ACTIVE'
  link: string                       // /projects/<projectId>/insurance#policy-<id>
}

function eventPriority(event: CalendarEvent): number {
  switch (event.type) {
    case 'PAYMENT':
      return 100
    // PR-NEW: insurance reminders share critical-ish priority so
    // they bubble up alongside payments and failed inspections.
    // Order from highest: EXPIRED > EXPIRATION > EXPIRING_SOON
    // (the EXPIRING_SOON bucket spans 30/15/7/1-day offsets;
    // severity in the event payload differentiates which color
    // the chip uses).
    case 'INSURANCE_EXPIRED':
      return 99
    case 'INSURANCE_EXPIRATION':
      return 96
    case 'ORDER_MATERIALS':
      return 90
    case 'INSPECTION_FAILED':
      return 95
    case 'INSPECTION_BLOCKED':
      return 94
    case 'INSPECTION_RETRY':
      return 92
    case 'INSURANCE_EXPIRING_SOON':
      return 91
    case 'INSPECTION_READY':
      return 89
    // Start-of-coverage is informational; sits below the urgent
    // insurance + inspection variants but above generic task
    // events so it's still visible at a glance.
    case 'INSURANCE_EFFECTIVE_START':
      return 55
    case 'SELECTION_DEADLINE':
      return 87
    case 'INSPECTION_SCHEDULED':
      return 88
    case 'INSPECTION_PASSED':
      return 45
    case 'RFI_DUE':
      return 80
    case 'CHANGE_ORDER':
      return 70
    case 'TASK_START':
      return 60
    case 'TASK_END':
      return 50
    default:
      return 0
  }
}

function eventSourceLabel(event: CalendarEvent, isEs: boolean): string {
  switch (event.type) {
    case 'PAYMENT':
      return isEs ? 'Pago' : 'Payment'
    case 'RFI_DUE':
      return 'RFI'
    case 'CHANGE_ORDER':
      return isEs ? 'Orden de cambio' : 'Change Order'
    case 'TASK_START':
    case 'TASK_END':
    case 'SELECTION_DEADLINE':
      return isEs ? 'Cronograma' : 'Schedule'
    case 'ORDER_MATERIALS':
      return isEs ? 'Orden de material' : 'Material Order'
    case 'INSPECTION_SCHEDULED':
    case 'INSPECTION_FAILED':
    case 'INSPECTION_RETRY':
    case 'INSPECTION_PASSED':
    case 'INSPECTION_READY':
    case 'INSPECTION_BLOCKED':
      return isEs ? 'Inspección' : 'Inspection'
    case 'INSURANCE_EXPIRING_SOON':
    case 'INSURANCE_EXPIRATION':
    case 'INSURANCE_EXPIRED':
    case 'INSURANCE_EFFECTIVE_START':
      return isEs ? 'Seguro' : 'Insurance'
    default:
      return isEs ? 'Fuente desconocida' : 'Unknown source'
  }
}

function eventPaymentStatusLabel(event: CalendarEvent, isEs: boolean): string | null {
  if (event.type !== 'PAYMENT') return null
  if (event.status === 'PAID') return isEs ? 'Confirmado' : 'Confirmed'
  return isEs ? 'Estimado' : 'Estimated'
}

function EventSourceChip({ event, isEs }: { event: CalendarEvent; isEs: boolean }) {
  const source = eventSourceLabel(event, isEs)
  const paymentStatus = eventPaymentStatusLabel(event, isEs)
  return (
    <span className="inline-flex items-center gap-1 flex-wrap">
      <span
        data-testid="event-source-label"
        className="text-[8px] font-[700] px-1.5 py-0.5 rounded bg-[#F2F0EB] text-[#6B6660]"
      >
        {source}
      </span>
      {paymentStatus && (
        <span
          data-testid="event-status-label"
          className="text-[8px] font-[700] px-1.5 py-0.5 rounded bg-[#EFF6FF] text-[#3B82F6]"
        >
          {paymentStatus}
        </span>
      )}
    </span>
  )
}

function eventActionLabel(event: CalendarEvent, isEs: boolean): string {
  switch (event.type) {
    case 'PAYMENT':
      return isEs ? 'Resolver pago' : 'Resolve payment'
    case 'ORDER_MATERIALS':
      return isEs ? 'Pedir materiales' : 'Order materials'
    case 'INSPECTION_FAILED':
      return isEs ? 'Resolver inspección fallida' : 'Resolve failed inspection'
    case 'INSPECTION_BLOCKED':
      return isEs ? 'Subir documentos faltantes' : 'Upload missing documents'
    case 'INSPECTION_RETRY':
      return isEs ? 'Reprogramar / retry' : 'Reschedule / retry'
    case 'INSPECTION_READY':
      return isEs ? 'Programar inspección' : 'Schedule inspection'
    case 'INSPECTION_SCHEDULED':
      return isEs ? 'Preparar inspección' : 'Prepare inspection'
    case 'SELECTION_DEADLINE':
      return isEs ? 'Resolver selección' : 'Resolve selection'
    case 'INSPECTION_PASSED':
      return isEs ? 'Registrar avance' : 'Record progress'
    case 'RFI_DUE':
      return isEs ? 'Responder RFI' : 'Answer RFI'
    case 'CHANGE_ORDER':
      return isEs ? 'Revisar cambio' : 'Review change'
    case 'TASK_START':
      return isEs ? 'Iniciar tarea' : 'Start task'
    case 'TASK_END':
      return isEs ? 'Cerrar tarea' : 'Close task'
    case 'INSURANCE_EXPIRED':
      return isEs ? 'Renovar seguro' : 'Renew insurance'
    case 'INSURANCE_EXPIRATION':
      return isEs ? 'Renovar seguro hoy' : 'Renew insurance today'
    case 'INSURANCE_EXPIRING_SOON':
      return isEs ? 'Programar renovación' : 'Schedule renewal'
    case 'INSURANCE_EFFECTIVE_START':
      return isEs ? 'Ver seguro' : 'View insurance'
    default:
      return isEs ? 'Revisar' : 'Review'
  }
}

export default function CalendarModule() {
  const { projectId } = useParams()
  const { t, i18n } = useTranslation()
  const { getToken } = useAuth()
  const isEs = i18n.language === 'es'
  const locale = isEs ? esLocale : undefined

  const [viewMode, setViewMode] = useState<ViewMode>('month')
  // Lazy init so the Date is constructed on first render instead of being
  // captured from a stale module-scope evaluation (fixes initial-range bug
  // where first fetch used the next month instead of today).
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date())

  // The date range we ask the backend for. For month/week we widen to the
  // visible cells (so partial weeks at the month edges still show events);
  // for day view we keep it tight.
  const range = useMemo(() => {
    if (viewMode === 'month') {
      return {
        from: startOfWeek(startOfMonth(currentDate), { weekStartsOn: 0 }),
        to: endOfWeek(endOfMonth(currentDate), { weekStartsOn: 0 }),
      }
    }
    if (viewMode === 'week') {
      return {
        from: startOfWeek(currentDate, { weekStartsOn: 0 }),
        to: endOfWeek(currentDate, { weekStartsOn: 0 }),
      }
    }
    return { from: currentDate, to: currentDate }
  }, [viewMode, currentDate])

  const fromKey = format(range.from, 'yyyy-MM-dd')
  const toKey = format(range.to, 'yyyy-MM-dd')

  // PR-NEW: the response carries `nextInsuranceRenewals` —
  // a range-independent shortlist of upcoming insurance
  // expirations the sidebar renders below "Next actions". The
  // month-grid event array stays bounded by [from, to]; this
  // list looks ahead.
  const { data, isLoading } = useQuery({
    queryKey: ['calendar-events', projectId, fromKey, toKey, i18n.language],
    queryFn: async () => {
      const token = await getToken()
      const res = await apiFetch(
        `/api/projects/${projectId}/calendar-events?from=${fromKey}&to=${toKey}&lang=${i18n.language}`,
        { headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}) } },
      )
      if (!res.ok) throw new Error('calendar-events failed')
      return res.json() as Promise<{
        events: CalendarEvent[]
        nextInsuranceRenewals?: NextInsuranceRenewal[]
      }>
    },
    enabled: !!projectId,
    retry: false,
  })

  const rawEvents: CalendarEvent[] = data?.events || []
  const nextInsuranceRenewals: NextInsuranceRenewal[] = data?.nextInsuranceRenewals || []

  // Apply FE-side task title localization once so all downstream renders are correct.
  // TASK_START / TASK_END titles are pre-constructed server-side with the stored
  // Spanish task name regardless of the requested language. localizeCalendarTaskTitle
  // maps known system task names to the active language; unknown names pass through.
  const events: CalendarEvent[] = useMemo(
    () => rawEvents.map(e =>
      (e.type === 'TASK_START' || e.type === 'TASK_END')
        ? { ...e, title: localizeCalendarTaskTitle(e.title, isEs) }
        : e
    ),
    [rawEvents, isEs],
  )

  const eventsByDate = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>()
    for (const e of events) {
      if (!map.has(e.date)) map.set(e.date, [])
      map.get(e.date)!.push(e)
    }
    return map
  }, [events])

  const todayKey = format(new Date(), 'yyyy-MM-dd')
  const todayEvents = (eventsByDate.get(todayKey) || []).slice().sort((a, b) => eventPriority(b) - eventPriority(a))
  // PR-NEW: filter INSURANCE_EFFECTIVE_START out of the
  // Action Center ("Nothing critical to resolve today") and the
  // Next actions panel. Coverage-start is informational only —
  // it should not be styled as something the user must
  // "resolve". The event still appears in the month grid
  // (eventsByDate) and in the neutral "Today" panel
  // (todayEvents), so users see it without it dominating the
  // critical surfaces.
  const isCriticalCandidate = (e: CalendarEvent) => e.type !== 'INSURANCE_EFFECTIVE_START'
  const todayPriorityEvents = todayEvents.filter(isCriticalCandidate).slice(0, 5)
  const upcomingPriorityEvents = events
    .filter(e => e.date >= todayKey)
    .filter(isCriticalCandidate)
    .slice()
    .sort((a, b) => {
      if (a.date !== b.date) return a.date.localeCompare(b.date)
      return eventPriority(b) - eventPriority(a)
    })
    .slice(0, 6)
  const orderReminders = events
    .filter(e => e.type === 'ORDER_MATERIALS')
    .slice()
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, 5)

  const goPrevious = () => {
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, -1))
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, -1))
    else setCurrentDate(addDays(currentDate, -1))
  }
  const goNext = () => {
    if (viewMode === 'month') setCurrentDate(addMonths(currentDate, 1))
    else if (viewMode === 'week') setCurrentDate(addWeeks(currentDate, 1))
    else setCurrentDate(addDays(currentDate, 1))
  }
  const goToday = () => setCurrentDate(new Date())

  return (
    <div className="p-5 md:p-6 max-w-[1400px] mx-auto">
      {/* HEADER */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h1 className="text-[22px] md:text-[28px] font-[900] tracking-[-0.8px] text-[#1C1A17]">
            {t('Project Calendar')}
          </h1>
          <p className="text-[12px] font-[600] text-[#9E9890] mt-0.5">
            {isEs
              ? 'Los elementos del calendario del cronograma y registros del proyecto aparecen aquí. Algunas fuentes son solo de planificación hasta confirmarse.'
              : 'Calendar items from project schedule and tracked project records appear here. Some sources are planning-only until confirmed.'}
          </p>
          {/* Financial Trust P0 (PR-F6): Calendar source honesty + not-connected note */}
          <div
            data-testid="calendar-trust-note"
            className="flex items-start gap-2 mt-2 rounded-lg border border-[#ECEAE4] bg-[#FAFAF8] px-3 py-2 max-w-xl"
          >
            <Info size={12} className="text-[#9E9890] mt-0.5 shrink-0" />
            <p className="text-[10px] font-[600] text-[#6B6660] leading-relaxed">
              {isEs
                ? 'Los eventos del calendario se generan desde el cronograma del proyecto: pagos con fecha límite, inspecciones, cambios, tareas y recordatorios de materiales. El calendario de pagos puede no estar completo si aún no hay contratos conectados.'
                : 'Calendar events are generated from the project schedule: payments with due dates, inspections, change orders, tasks, and material reminders. Payment schedule may be incomplete if contracts are not yet connected.'}
            </p>
          </div>
        </div>

        <div className="flex gap-1 bg-[#F2F0EB] rounded-full p-1 self-start md:self-auto">
          {(['month', 'week', 'day'] as ViewMode[]).map(mode => (
            <button
              key={mode}
              onClick={() => setViewMode(mode)}
              className={`px-4 py-1.5 text-[11px] font-[800] rounded-full transition-colors ${
                viewMode === mode
                  ? 'bg-[#06539B] text-white'
                  : 'text-[#9E9890] hover:text-[#1C1A17]'
              }`}
            >
              {mode === 'month' ? t('Month') : mode === 'week' ? t('Week') : t('Day')}
            </button>
          ))}
        </div>
      </div>

      {/* NAVIGATION BAR */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button onClick={goPrevious} className="p-2 rounded-full hover:bg-[#F2F0EB]" aria-label={isEs ? 'Anterior' : 'Previous'}>
            <ChevronLeft size={18} className="text-[#1C1A17]" />
          </button>
          <button
            onClick={goToday}
            className="px-4 py-1.5 text-[11px] font-[800] border border-[#ECEAE4] rounded-full hover:bg-[#F2F0EB]"
          >
            {t('Today')}
          </button>
          <button onClick={goNext} className="p-2 rounded-full hover:bg-[#F2F0EB]" aria-label={isEs ? 'Siguiente' : 'Next'}>
            <ChevronRight size={18} className="text-[#1C1A17]" />
          </button>
        </div>
        <h2 className="text-[16px] md:text-[18px] font-[800] text-[#1C1A17]">
          {format(currentDate, viewMode === 'day' ? (isEs ? "EEEE, d 'de' MMMM yyyy" : 'EEEE, MMMM d yyyy') : 'MMMM yyyy', { locale })}
        </h2>
        <div className="text-[11px] font-[700] text-[#9E9890]">
          {isLoading ? '…' : `${events.length} ${t('events')}`}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* MAIN GRID */}
        <div className="lg:col-span-9">
          {viewMode === 'month' && (
            <MonthView currentDate={currentDate} eventsByDate={eventsByDate} isEs={isEs} t={t} />
          )}
          {viewMode === 'week' && (
            <WeekView currentDate={currentDate} eventsByDate={eventsByDate} isEs={isEs} />
          )}
          {viewMode === 'day' && (
            <DayView
              currentDate={currentDate}
              events={eventsByDate.get(format(currentDate, 'yyyy-MM-dd')) || []}
              isEs={isEs}
              t={t}
            />
          )}
        </div>

        {/* SIDEBAR */}
        <div className="lg:col-span-3 space-y-4">
          <div className="bg-[#EFF6FF] border border-[#BFDBFE] rounded-xl p-4">
            <h3 className="text-[13px] font-[800] text-[#1E3A8A] mb-3">
              {t('Action Center')}
            </h3>
            {todayPriorityEvents.length === 0 ? (
              <p className="text-[11px] font-[600] text-[#4B5563]">{t('Nothing critical to resolve today')}</p>
            ) : (
              <div className="space-y-2">
                {todayPriorityEvents.map(e => (
                  <PriorityEventCard key={e.id} event={e} isEs={isEs} />
                ))}
              </div>
            )}
          </div>

          {/* Order reminders panel — NEW */}
          {orderReminders.length > 0 && (
            <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
              <h3 className="text-[13px] font-[800] text-orange-900 mb-3 flex items-center gap-1.5">
                <Package size={14} />
                {t('Material Orders')}
              </h3>
              <div className="space-y-2">
                {orderReminders.map(e => <EventCard key={e.id} event={e} showDate isEs={isEs} />)}
              </div>
            </div>
          )}

          {/* Today panel */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
            <h3 className="text-[13px] font-[800] text-[#1C1A17] mb-3 flex items-center gap-1.5">
              <CalendarIcon size={14} className="text-[#06539B]" />
              {t('Today')}
            </h3>
            {todayEvents.length === 0 ? (
              <p className="text-[11px] font-[600] text-[#9E9890]">{t('Nothing pending today')}</p>
            ) : (
              <div className="space-y-2">
                {todayEvents.map(e => <EventCard key={e.id} event={e} isEs={isEs} />)}
              </div>
            )}
          </div>

          {/* Upcoming panel */}
          <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
            <h3 className="text-[13px] font-[800] text-[#1C1A17] mb-3 flex items-center gap-1.5">
              <ChevronRight size={14} className="text-[#06539B]" />
              {t('Next actions')}
            </h3>
            {upcomingPriorityEvents.length === 0 ? (
              <p className="text-[11px] font-[600] text-[#9E9890]">{t('No upcoming events')}</p>
            ) : (
              <div className="space-y-2">
                {upcomingPriorityEvents.map(e => <PriorityEventCard key={e.id} event={e} showDate isEs={isEs} />)}
              </div>
            )}
          </div>

          {/* PR-NEW: Next insurance renewal panel. Range-INDEPENDENT
              shortlist of the project's upcoming insurance
              expirations — useful when the visible month grid
              doesn't contain the next expiration date (e.g.
              builder viewing May 2026 sees their Builder's Risk
              policy expires in May 2027). Informational only;
              never duplicated as month-grid chips. */}
          {nextInsuranceRenewals.length > 0 && (
            <div className="bg-white border border-[#ECEAE4] rounded-xl p-4">
              <h3 className="text-[13px] font-[800] text-[#1C1A17] mb-3 flex items-center gap-1.5">
                <Shield size={14} className="text-[#06539B]" />
                {isEs ? 'Próximo vencimiento de seguro' : 'Next insurance renewal'}
              </h3>
              <div className="space-y-2">
                {nextInsuranceRenewals.map((r) => {
                  const isCritical = r.derivedStatus === 'EXPIRED' || (r.derivedStatus === 'EXPIRING_SOON' && r.daysUntilExpiration <= 7)
                  const isWarning = r.derivedStatus === 'EXPIRING_SOON' && r.daysUntilExpiration > 7
                  const dotColor = isCritical ? '#EF4444' : isWarning ? '#F59E0B' : '#06539B'
                  const dateLabel = new Date(r.endDate).toLocaleDateString(isEs ? 'es-MX' : 'en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  return (
                    <a
                      key={r.policyId}
                      href={r.link}
                      className="flex items-start gap-2 text-[11px] hover:bg-[#FAFAF8] rounded-md px-1.5 py-1 -mx-1.5 transition-colors"
                    >
                      <span className="mt-1 h-2 w-2 rounded-full shrink-0" style={{ background: dotColor }} />
                      <div className="flex-1 min-w-0">
                        <div className="font-[800] text-[#1C1A17] truncate">{r.policyTypeLabel}</div>
                        <div className="font-[600] text-[#9E9890] truncate">{r.carrier}</div>
                        <div className="font-[600] text-[#4B5563]">
                          {dateLabel}
                          <span className="ml-1 text-[#9E9890]">· {r.daysUntilExpiration} {isEs ? (r.daysUntilExpiration === 1 ? 'día' : 'días') : (r.daysUntilExpiration === 1 ? 'day' : 'days')}</span>
                        </div>
                      </div>
                    </a>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── SUB-COMPONENTS ────────────────────────────────────────────────────

function MonthView({
  currentDate, eventsByDate, isEs, t,
}: { currentDate: Date; eventsByDate: Map<string, CalendarEvent[]>; isEs: boolean; t: (s: string) => string }) {
  const monthStart = startOfMonth(currentDate)
  const monthEnd = endOfMonth(currentDate)
  const calStart = startOfWeek(monthStart, { weekStartsOn: 0 })
  const calEnd = endOfWeek(monthEnd, { weekStartsOn: 0 })

  const days: Date[] = []
  for (let d = calStart; d <= calEnd; d = addDays(d, 1)) days.push(d)

  const weekdayLabels = isEs
    ? ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb']
    : ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="grid grid-cols-7 border-b border-[#ECEAE4]">
        {weekdayLabels.map(label => (
          <div
            key={label}
            className="px-2 py-2 text-[10px] font-[800] uppercase tracking-[0.5px] text-[#9E9890] text-center"
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const dayKey = format(day, 'yyyy-MM-dd')
          const dayEvents = eventsByDate.get(dayKey) || []
          const inCurrentMonth = isSameMonth(day, currentDate)
          const today = isToday(day)

          return (
            <div
              key={i}
              className={`min-h-[110px] border-r border-b border-[#ECEAE4] p-2 ${
                inCurrentMonth ? 'bg-white' : 'bg-[#FAFAF8]'
              } ${today ? 'bg-[#06539B]/5' : ''}`}
            >
              <div
                className={`text-[12px] font-[700] mb-1 ${
                  today ? 'text-[#06539B]' : inCurrentMonth ? 'text-[#1C1A17]' : 'text-[#9E9890]'
                }`}
              >
                {format(day, 'd')}
                {today && (
                  <span className="ml-1 text-[9px] font-[800]">{isEs ? 'HOY' : 'TODAY'}</span>
                )}
              </div>
              <div className="space-y-0.5">
                {dayEvents.slice(0, 3).map(e => (
                  <Link
                    key={e.id}
                    to={e.link}
                    className="block text-[9px] font-[700] truncate px-1.5 py-0.5 rounded text-white hover:opacity-90"
                    style={{ backgroundColor: e.color }}
                    title={e.title + (e.description ? ' — ' + e.description : '')}
                  >
                    {e.title}
                  </Link>
                ))}
                {dayEvents.length > 3 && (
                  <div className="text-[9px] font-[700] text-[#9E9890] px-1.5">
                    +{dayEvents.length - 3} {t('more')}
                  </div>
                )}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function WeekView({
  currentDate, eventsByDate, isEs,
}: { currentDate: Date; eventsByDate: Map<string, CalendarEvent[]>; isEs: boolean }) {
  const weekStart = startOfWeek(currentDate, { weekStartsOn: 0 })
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i))
  const locale = isEs ? esLocale : undefined

  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl overflow-hidden">
      <div className="grid grid-cols-7">
        {days.map((day, i) => {
          const dayKey = format(day, 'yyyy-MM-dd')
          const dayEvents = eventsByDate.get(dayKey) || []
          const today = isToday(day)

          return (
            <div
              key={i}
              className={`border-r border-[#ECEAE4] last:border-r-0 ${today ? 'bg-[#06539B]/5' : ''}`}
            >
              <div className="px-3 py-3 border-b border-[#ECEAE4]">
                <div className="text-[10px] font-[800] uppercase text-[#9E9890]">
                  {format(day, 'EEE', { locale })}
                </div>
                <div
                  className={`text-[20px] font-[900] mt-0.5 ${
                    today ? 'text-[#06539B]' : 'text-[#1C1A17]'
                  }`}
                >
                  {format(day, 'd')}
                </div>
              </div>
              <div className="p-2 space-y-1 min-h-[400px]">
                {dayEvents.map(e => (
                  <Link
                    key={e.id}
                    to={e.link}
                    className="block text-[10px] font-[700] px-2 py-1.5 rounded text-white hover:opacity-90"
                    style={{ backgroundColor: e.color }}
                  >
                    <div className="truncate">{e.title}</div>
                    {typeof e.amount === 'number' && (
                      <div className="text-[9px] opacity-90 mt-0.5">{formatMoneyValue(e.amount, isEs)}</div>
                    )}
                    {typeof e.itemCount === 'number' && (
                      <div className="text-[9px] opacity-90 mt-0.5">
                        {e.itemCount} {isEs ? 'artículos' : 'items'}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

function DayView({
  currentDate, events, isEs, t,
}: { currentDate: Date; events: CalendarEvent[]; isEs: boolean; t: (s: string) => string }) {
  const locale = isEs ? esLocale : undefined
  return (
    <div className="bg-white border border-[#ECEAE4] rounded-xl p-6">
      <h3 className="text-[18px] font-[800] text-[#1C1A17] mb-4">
        {format(currentDate, isEs ? "EEEE, d 'de' MMMM" : 'EEEE, MMMM d', { locale })}
      </h3>
      {events.length === 0 ? (
        <div className="text-center py-12">
          <CalendarIcon size={48} className="mx-auto text-[#ECEAE4] mb-3" />
          <p className="text-[12px] font-[700] text-[#9E9890]">{t('No events on this day')}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {events.map(e => (
            <Link
              key={e.id}
              to={e.link}
              className="flex items-start gap-3 p-3 border border-[#ECEAE4] rounded-lg hover:bg-[#FAFAF8]"
            >
              <div className="w-1 self-stretch rounded-full" style={{ backgroundColor: e.color }} />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-[800] text-[#1C1A17]">{e.title}</div>
                {e.description && (
                  <div className="text-[11px] font-[600] text-[#9E9890] mt-0.5">{e.description}</div>
                )}
                <div className="mt-1">
                  <EventSourceChip event={e} isEs={isEs} />
                </div>
              </div>
              {typeof e.amount === 'number' && (
                <div className="text-[13px] font-[800] text-[#06539B]">{formatMoneyValue(e.amount, isEs)}</div>
              )}
            </Link>
          ))}
        </div>
      )}
    </div>
  )
}

function EventCard({
  event, showDate = false, isEs,
}: { event: CalendarEvent; showDate?: boolean; isEs: boolean }) {
  const locale = isEs ? esLocale : undefined
  return (
    <Link to={event.link} className="block p-2 border border-[#ECEAE4] rounded-lg hover:bg-white">
      <div className="flex items-start gap-2">
        <div className="w-1 self-stretch rounded-full mt-0.5" style={{ backgroundColor: event.color }} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-[800] text-[#1C1A17] truncate">{event.title}</div>
          {event.description && (
            <div className="text-[9px] font-[600] text-[#9E9890] mt-0.5 line-clamp-2">{event.description}</div>
          )}
          <div className="mt-1">
            <EventSourceChip event={event} isEs={isEs} />
          </div>
          {showDate && (
            <div className="text-[9px] font-[700] text-[#9E9890] mt-0.5">
              {format(new Date(event.date), 'd MMM', { locale })}
            </div>
          )}
          {typeof event.amount === 'number' && (
            <div className="text-[10px] font-[700] text-[#06539B] mt-0.5">{formatMoneyValue(event.amount, isEs)}</div>
          )}
        </div>
      </div>
    </Link>
  )
}

function PriorityEventCard({
  event, showDate = false, isEs,
}: { event: CalendarEvent; showDate?: boolean; isEs: boolean }) {
  const locale = isEs ? esLocale : undefined
  return (
    <Link to={event.link} className="block p-3 border border-[#DCE7F7] rounded-lg bg-white hover:bg-[#F8FBFF]">
      <div className="flex items-start gap-2">
        <div className="w-1.5 self-stretch rounded-full mt-0.5" style={{ backgroundColor: event.color }} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2">
            <div className="text-[11px] font-[900] text-[#1C1A17] truncate">{event.title}</div>
            <div className="text-[9px] font-[800] text-[#06539B] whitespace-nowrap">
              {eventActionLabel(event, isEs)}
            </div>
          </div>
          {event.description && (
            <div className="text-[9px] font-[600] text-[#6B7280] mt-1 line-clamp-2">{event.description}</div>
          )}
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <EventSourceChip event={event} isEs={isEs} />
            {showDate && (
              <div className="text-[9px] font-[700] text-[#9E9890]">
                {format(new Date(event.date), 'd MMM', { locale })}
              </div>
            )}
            {typeof event.amount === 'number' && (
              <div className="text-[10px] font-[800] text-[#06539B]">{formatMoneyValue(event.amount, isEs)}</div>
            )}
            {typeof event.itemCount === 'number' && (
              <div className="text-[9px] font-[700] text-[#9E9890]">
                {event.itemCount} {isEs ? 'artículos' : 'items'}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  )
}
